import React, { FC, ReactElement, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Dropdown } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import { WithDelayedChange } from '../common/WithDelayedChange';
import { useGetSearchResult } from '../../../loaders/useGetSearchResult';
import sass from './SearchBox.module.sass';
import { Link } from 'react-router-dom';
import { useGetEntitiesLink } from '../../routes';
import { rootStore } from '../../../model/RootStoreContext';
import { ISearchResultVm } from '@yakoffice/publisher-types';
import { SearchResultTableRow } from './SearchResultTableRow';
import { SearchResultTable } from './SearchResultTable';

export const SearchBox: FC = observer(() => {

    const [showMenu, setShowMenu] = useState(true);
    const [searchValue, setSearchValue] = useState('');
    const [searchLimit, setSearchLimit] = useState(100);
    const [searchAllGameEnvironments, setSearchAllGameEnvironments] = useState(false);
    const searchResult = useGetSearchResult(searchValue, searchAllGameEnvironments, searchLimit);
    const getEntitiesLink = useGetEntitiesLink();

    const handleSearchValueUpdate = (value: string) => {
        setSearchValue(value);
        setShowMenu(true);
    };

    const autoFocus = (element: any) => element?.focus();

    const handleGameEnvironmentSwitch = () => {
        setSearchAllGameEnvironments(!searchAllGameEnvironments);
        setShowMenu(true);
    };

    const renderForGameEnvironments = (entitiesResults: ISearchResultVm[]) => {

        const gameEnvironments = rootStore.gameEnvironmentsStore.gameEnvironments.map(gameEnvironment => {
            return { name: gameEnvironment.name, id: gameEnvironment.id };
        });

        const renderTheseElements: ReactElement[] = [];

        for (const gameEnvironment of gameEnvironments) {

            const searchResultsForGameEnvironment = entitiesResults.filter(searchResult => searchResult.gameEnvironmentId === gameEnvironment.id);

            if (searchResultsForGameEnvironment.length > 0) {
                renderTheseElements.push(
                    <SearchResultTable body={searchResultsForGameEnvironment.map(searchResult => <SearchResultTableRow
                        searchResult={searchResult} key={searchResult.entityId} />)} head={gameEnvironment.name} />
                );
            }
        }

        return renderTheseElements;
    };

    const renderEntityResults = (entitiesResults: ISearchResultVm[]) => {

        if (searchAllGameEnvironments)
            return renderForGameEnvironments(entitiesResults);

        else
            return <SearchResultTable
                body={entitiesResults.map(searchResult => <SearchResultTableRow searchResult={searchResult}
                                                                                key={searchResult.entityId} />)} />;
    };


    const renderSearchResults = () => {

        const entitiesResults = searchResult.searchResults.filter(searchResult => searchResult.entityId);

        const kindsResults = searchResult.searchResults.filter(searchResult => searchResult.kindId && searchResult.kindName?.includes(searchValue)).filter((value, index, self) =>
            index === self.findIndex((t) => (
                t.kindName === value.kindName
            )));

        return (
            <div>
                {kindsResults.length > 0 &&
                    <>
                        <h6>Kinds</h6>
                        <SearchResultTable body={
                            kindsResults.map(searchResult => {
                                return (
                                    searchResult.kindId && !searchResult.kindIsSpecificationForKinds && (searchResult.gameEnvironmentId || rootStore.gameEnvironmentsStore.currentGameEnvironment) &&
                                    <tr key={searchResult.kindId.toString()}>
                                        <td>
                                            <Link to={getEntitiesLink({
                                                categoryId: searchResult.categoryId,
                                                kindId: searchResult.kindId,
                                                gameEnvironmentId: searchResult.gameEnvironmentId ?? undefined
                                            })
                                            }>{searchResult.kindName}</Link>
                                        </td>
                                    </tr>
                                );
                            })
                        } />
                    </>
                }
                {entitiesResults.length > 0 &&
                    <>
                        <h6>Entities</h6>
                        {renderEntityResults(entitiesResults)}
                    </>
                }
            </div>
        );
    };

    return (
        <div className={sass.searchBox}>
            <Form.Check
                type="switch"
                id="game-environment-switch"
                label="Search all game environments"
                checked={searchAllGameEnvironments}
                onChange={handleGameEnvironmentSwitch}
                className="mb-2"
            />

            {/*<hr />*/}

            <WithDelayedChange waitInterval={500} delayedOnChange={handleSearchValueUpdate}>{eventHandlers =>
                <Form.Control
                    type="text"
                    placeholder="Search..."
                    onChange={eventHandlers.handleOnChange}
                    className="mt-3 mb-3"
                    autoFocus={true}
                    ref={autoFocus}
                />
            }
            </WithDelayedChange>
            <Form.Text id="passwordHelpBlock" muted>Search limit</Form.Text>
            <Form.Select size="sm"
                         id="searchLimit"
                         aria-describedby="passwordHelpBlock"
                         onChange={(e) => setSearchLimit(parseInt(e.target.value, 10))}
                         defaultValue={searchLimit.toString()}>
                <option value="25">25</option>
                <option value="100">100</option>
                <option value="200">200</option>
            </Form.Select>
            {searchResult.isLoaded &&
                <Dropdown className={sass.searchResults} show={showMenu}>
                    <Dropdown.Menu className={sass.dropdownMenu} show={searchResult.isLoaded}>
                        {searchResult.searchResults.length === 0
                            ? <div key="notFound"
                                   className={sass.menuItem}>{`No results found for ${searchValue}...`}</div>
                            : renderSearchResults()
                        }
                    </Dropdown.Menu>
                </Dropdown>
            }
        </div>
    );
});
